import React from 'react';
import { PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { Button } from '@oysterjs/ui/button';
import { ScheduleSendMenu } from './artifact-focus-email-schedule-menu';

const SEND_DELAY_MS = 5000;

export const EmailSendButton = (props: {
  disabledReason?: string;
  sending: boolean;
  cancelling?: boolean;
  // A local display deadline from the composer, not the server's timestamp.
  sendAfter?: string | Date | null;
  onSend: () => Promise<void>;
  onCancel: () => Promise<void>;
  /** Adds the Schedule send arrow; absent while scheduled sending is off. */
  schedule?: {
    onSchedule: (sendAt: Date) => void;
    onPickDateAndTime: () => void;
  };
}) => {
  const deadline = props.sendAfter ? new Date(props.sendAfter).getTime() : null;
  const remainingAt = () => (deadline === null ? null : Math.max(0, deadline - Date.now()));
  const [remaining, setRemaining] = React.useState(remainingAt);
  const [action, setAction] = React.useState<'send' | 'cancel' | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const active = React.useRef(true);
  const busy = React.useRef({ send: false, cancel: false });

  React.useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);

  // This clock only paints the button. The server owns delivery and cancellation,
  // so an unmounted or throttled browser cannot change the accepted send.
  React.useEffect(() => {
    const initial = deadline === null ? null : Math.max(0, deadline - Date.now());
    setRemaining(initial);
    if (deadline === null || initial === 0) return;
    const timer = window.setInterval(() => {
      const next = Math.max(0, deadline - Date.now());
      setRemaining(next);
      if (next === 0) window.clearInterval(timer);
    }, 100);
    return () => window.clearInterval(timer);
  }, [deadline]);

  const cancellable = deadline !== null && (remaining ?? 0) > 0;
  const click = async () => {
    const cancel = deadline !== null && deadline > Date.now();
    const operation = cancel ? 'cancel' : 'send';
    if (busy.current[operation] || props.cancelling) return;
    if (!cancel && (props.sending || props.disabledReason || deadline !== null)) return;
    busy.current[operation] = true;
    setAction(operation);
    setError(null);
    try {
      if (cancel) await props.onCancel();
      else await props.onSend();
    } catch {
      if (active.current) {
        setError(
          cancel
            ? 'We could not cancel this send. It may still be sent.'
            : 'We could not queue this email. Please try again.'
        );
      }
    } finally {
      busy.current[operation] = false;
      if (active.current) setAction((current) => (current === operation ? null : current));
    }
  };

  const seconds = Math.ceil((remaining ?? 0) / 1000);
  const working =
    props.cancelling ||
    action === 'cancel' ||
    (!cancellable && (action === 'send' || props.sending || deadline !== null));
  const label =
    action === 'cancel' || props.cancelling
      ? 'Cancelling…'
      : cancellable
        ? 'Cancel send'
        : working
          ? 'Sending…'
          : 'Send now';
  const showScheduleMenu = !!props.schedule && !cancellable && !working;
  return (
    <div className="flex flex-wrap items-center justify-end gap-y-1">
      {/* The arrow stays joined to Send now; only the error line wraps. */}
      <div className="inline-flex flex-nowrap">
        <Button
          type="button"
          color="sky"
          className={showScheduleMenu ? 'rounded-r-none' : undefined}
          disabled={working || (!cancellable && !!props.disabledReason)}
          loading={working}
          title={cancellable ? undefined : props.disabledReason}
          onClick={() => void click()}
          aria-label={label}
        >
          {!cancellable ? (
            <PaperAirplaneIcon className="size-4" />
          ) : (
            <svg aria-hidden="true" className="size-4 -rotate-90" viewBox="0 0 20 20">
              <circle
                cx="10"
                cy="10"
                r="8"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                opacity="0.3"
              />
              <circle
                cx="10"
                cy="10"
                r="8"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                pathLength="100"
                strokeDasharray="100"
                strokeDashoffset={
                  100 * (1 - Math.min(remaining ?? 0, SEND_DELAY_MS) / SEND_DELAY_MS)
                }
                className="transition-all duration-100 ease-linear motion-reduce:transition-none"
              />
            </svg>
          )}
          <span aria-hidden="true">
            {label === 'Cancel send' ? `Send in ${seconds}s · Cancel` : label}
          </span>
        </Button>
        {showScheduleMenu && props.schedule && (
          <ScheduleSendMenu
            disabledReason={props.disabledReason}
            sendNowDescription="5 seconds to undo"
            onSendNow={() => void click()}
            onSchedule={props.schedule.onSchedule}
            onPickDateAndTime={props.schedule.onPickDateAndTime}
          />
        )}
      </div>
      <span role="status" aria-atomic="true" className="sr-only">
        {label === 'Cancel send'
          ? 'Email queued. Cancel send is available.'
          : label === 'Send now'
            ? ''
            : label}
      </span>
      {error && (
        <p role="alert" className="basis-full text-right text-xs text-red-600 dark:text-red-500">
          {error}
        </p>
      )}
    </div>
  );
};
